import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { ConnectionStatus, SubscriptionConnectionDTO } from '../subscriptionConnection.types';
import { parseJSON } from '@utils/CommonUtil';
import { SubscriptionSimpleModel } from '@models/SubscriptionSimpleModel';
import { TenantSimpleModel } from '@repositories/tenantRepository/models/TenantSimpleModel';
import { ConnectionRespModel } from './ConnectionRespModel';
import { ProductConnectorModel } from '@repositories/productConnectorRepository/models/ProductConnectorModel';

export class SubscriptionConnectionModel {
  private readonly _dto: SubscriptionConnectionDTO;
  private readonly _connection: ConnectionRespModel;
  private readonly _tenant?: TenantSimpleModel;
  private readonly _connector?: ProductConnectorModel;
  private readonly _subscription: SubscriptionSimpleModel;

  constructor(dto: SubscriptionConnectionDTO) {
    this._dto = dto;

    this._connection = new ConnectionRespModel(dto.connection || {});
    this._connector = dto.connector ? new ProductConnectorModel(dto.connector) : undefined;
    this._tenant = dto.connector ? new TenantSimpleModel(dto.tenant) : undefined;
    this._subscription = new SubscriptionSimpleModel(dto.subscription);
  }

  get originDTO(): SubscriptionConnectionDTO {
    return this._dto;
  }

  get isConnectionExist() {
    return !!this._dto.status;
  }

  get creator() {
    return this._dto.creator;
  }

  get productName() {
    return this._dto.productName;
  }

  get workflowCount() {
    return this._dto.workflowCount;
  }

  get connection() {
    return this._connection;
  }

  get addUserSchemaObject() {
    return parseJSON<UnknownObject>(this.connection.connector.addUserSchema);
  }

  get addUserSchemaUserRoles(): string[] {
    return this.addUserSchemaObject?.properties?.userRole?.enum ?? [];
  }

  get connectionId() {
    return this._dto.connectionId || '';
  }

  get status(): ConnectionStatus {
    return this._dto.status || 'DISCONNECTED';
  }

  get statusMessage() {
    return this._dto.statusMessage || '';
  }

  get subscription() {
    return this._subscription;
  }

  get connector() {
    return this._connector;
  }

  get tenant() {
    return this._tenant;
  }

  get remoteConnectionId() {
    return this._dto.remoteConnectionId || '';
  }

  get createdBy() {
    return this._dto.createdBy || '';
  }

  get createdDate(): Dayjs {
    return dayjs(this._dto.createdDate);
  }

  get updatedBy() {
    return this._dto.updatedBy || '';
  }

  get updatedDate(): Dayjs {
    return dayjs(this._dto.updatedDate);
  }

  get isConnected() {
    return this.status === 'CONNECTED';
  }

  get isManuallyUserAddable() {
    return !!this.connection.connector.actions.find(({ method }) => method === 'addUser');
  }

  get isManuallyUserRemovable() {
    return !!this.connection.connector.actions.find(({ method }) => method === 'removeUser');
  }

  get isManuallyGetLicense() {
    return !!this.connection.connector.actions.find(({ method }) => method === 'getLicense');
  }

  get isManuallyAddLicense() {
    return !!this.connection.connector.actions.find(({ method }) => method === 'addLicense');
  }

  get isManuallyRemoveLicese() {
    return !!this.connection.connector.actions.find(({ method }) => method === 'removeLicense');
  }

  get isManuallyUserRevokeLicense() {
    return !!this.connection.connector.actions.find(({ method }) => method === 'revokeLicense');
  }

  get isManuallyUserAssignLicense() {
    return !!this.connection.connector.actions.find(({ method }) => method === 'assignLicense');
  }
}
